import API_BASE_URL from "../config/apiConfig";
import createApiClient from "../config/apiClient";

const UPLOAD_API_BASE_URL = `${API_BASE_URL}/upload`;

// Tạo apiClient riêng cho upload
const uploadApiClient = createApiClient(UPLOAD_API_BASE_URL);

/**
 * Upload file chung cho các object khác nhau
 * @param {File} file - File cần upload
 * @param {string} objectId - ID của object liên quan
 * @param {string} objectType - Loại object (Project, ProjectGallery, News, Banner, etc.)
 * @param {string} [objectField] - Phân biệt field con (vd: AboutPage section: "story", "brandIdentity", "services-0")
 * @returns {Promise} - Response từ server
 */
const uploadFile = async (file, objectId, objectType, objectField) => {
  try {
    if (!objectId) {
      throw new Error("Object ID is required");
    }

    if (!objectType) {
      throw new Error("Object type is required");
    }

    const formData = new FormData();
    formData.append("image", file);
    formData.append("objectId", objectId);
    formData.append("objectType", objectType);
    if (objectField) {
      formData.append("objectField", objectField);
    }

    const response = await uploadApiClient.post('/image', formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 30000,
    });

    // Xử lý response an toàn hơn
    if (!response.data) {
      throw new Error("No response data from server");
    }

    if (!response.data.success && response.data.message) {
      throw new Error(response.data.message);
    }

    return response.data;
  } catch (err) {
    console.error("Upload file error details:", err);

    // Xử lý lỗi chi tiết hơn
    if (err.code === "ECONNREFUSED" || err.message?.includes("ECONNREFUSED")) {
      throw new Error("Không thể kết nối đến server. Vui lòng thử lại sau.");
    }

    if (err.response?.data?.message) {
      throw new Error(err.response.data.message);
    }

    if (err.message) {
      throw new Error(err.message);
    }

    throw new Error("Upload file failed due to server error");
  }
};

/**
 * Lấy danh sách file upload với phân trang
 * @param {Object} params - Tham số query
 * @param {number} params.page - Trang hiện tại (mặc định: 1)
 * @param {number} params.limit - Số item mỗi trang (mặc định: 10)
 * @param {string} params.folder - Lọc theo folder
 * @param {string} params.fileObjectType - Lọc theo object type
 * @param {string} params.search - Tìm kiếm theo tên file
 * @param {string} params.sortBy - Field để sort (mặc định: createdAt)
 * @param {string} params.sortOrder - asc hoặc desc (mặc định: desc)
 * @returns {Promise} - Response từ server
 */
const getList = async (params = {}) => {
  try {
    const {
      page = 1,
      limit = 10,
      folder = '',
      fileObjectType = '',
      search = '',
      sortBy = 'createdAt',
      sortOrder = 'desc'
    } = params;

    const queryParams = new URLSearchParams();
    
    if (page) queryParams.append('page', page);
    if (limit) queryParams.append('limit', limit);
    if (folder) queryParams.append('folder', folder);
    if (fileObjectType) queryParams.append('fileObjectType', fileObjectType);
    if (search) queryParams.append('search', search);
    if (sortBy) queryParams.append('sortBy', sortBy);
    if (sortOrder) queryParams.append('sortOrder', sortOrder);

    const response = await uploadApiClient.get(`/list?${queryParams.toString()}`);

    if (!response.data) {
      throw new Error("No response data from server");
    }

    if (!response.data.success && response.data.message) {
      throw new Error(response.data.message);
    }

    return response.data;
  } catch (err) {
    console.error("Get list uploads error details:", err);

    if (err.code === "ECONNREFUSED" || err.message?.includes("ECONNREFUSED")) {
      throw new Error("Không thể kết nối đến server. Vui lòng thử lại sau.");
    }

    if (err.response?.data?.message) {
      throw new Error(err.response.data.message);
    }

    if (err.message) {
      throw new Error(err.message);
    }

    throw new Error("Get list uploads failed due to server error");
  }
};

/**
 * Lấy file công khai (logo hoặc favicon)
 * @param {string} type - Loại file (logo hoặc favicon)
 * @returns {Promise} - Response từ server chứa fullUrl
 */
const getPublicFile = async (type) => {
  try {
    if (!type) {
      throw new Error("Type is required (logo or favicon)");
    }

    const response = await uploadApiClient.get(`/public/${type}`);

    if (!response.data) {
      throw new Error("No response data from server");
    }

    if (!response.data.success && response.data.message) {
      throw new Error(response.data.message);
    }

    return response.data;
  } catch (err) {
    console.error("Get public file error details:", err);

    if (err.code === "ECONNREFUSED" || err.message?.includes("ECONNREFUSED")) {
      throw new Error("Không thể kết nối đến server. Vui lòng thử lại sau.");
    }

    if (err.response?.data?.message) {
      throw new Error(err.response.data.message);
    }

    if (err.message) {
      throw new Error(err.message);
    }

    throw new Error("Get public file failed due to server error");
  }
};

/**
 * Backup tất cả files từ MinIO và tải xuống file ZIP
 * @returns {Promise<void>}
 */
const backupFiles = async () => {
  try {
    const response = await uploadApiClient.get('/backup', {
      responseType: 'blob', // Quan trọng: cần responseType là 'blob' để nhận file binary
      timeout: 300000, // 5 phút timeout vì backup có thể mất nhiều thời gian
    });

    // Kiểm tra nếu response là error JSON trong blob
    if (response.data.type && response.data.type.includes('application/json')) {
      const reader = new FileReader();
      const errorText = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsText(response.data);
      });
      
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.message || "Backup thất bại");
      } catch (parseError) {
        throw new Error("Backup thất bại: Lỗi không xác định");
      }
    }

    // Tạo URL từ blob
    const blob = new Blob([response.data], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    
    // Tạo link để download
    const link = document.createElement('a');
    link.href = url;
    
    // Lấy tên file từ Content-Disposition header hoặc dùng tên mặc định
    const contentDisposition = response.headers['content-disposition'];
    let fileName = 'stackwayFile-backup.zip';
    
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
      if (fileNameMatch && fileNameMatch[1]) {
        // Remove quotes nếu có
        fileName = fileNameMatch[1].replace(/['"]/g, '');
      }
    }
    
    // Đảm bảo file name có extension .zip
    if (!fileName.endsWith('.zip')) {
      fileName = fileName.endsWith('.zip_') 
        ? fileName.replace('.zip_', '.zip') 
        : `${fileName}.zip`;
    }
    
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true, message: 'Backup thành công và đã tải xuống' };
  } catch (err) {
    console.error("Backup files error details:", err);

    if (err.code === "ECONNREFUSED" || err.message?.includes("ECONNREFUSED")) {
      throw new Error("Không thể kết nối đến server. Vui lòng thử lại sau.");
    }

    // Xử lý lỗi từ server (có thể là blob chứa JSON error)
    if (err.response?.data) {
      if (err.response.data instanceof Blob && err.response.data.type?.includes('application/json')) {
        const reader = new FileReader();
        const errorText = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsText(err.response.data);
        });
        
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.message || "Backup thất bại");
        } catch (parseError) {
          throw new Error("Backup thất bại: Lỗi không xác định");
        }
      }
      
      if (err.response.data.message) {
        throw new Error(err.response.data.message);
      }
    }

    if (err.message) {
      throw new Error(err.message);
    }

    throw new Error("Backup files failed due to server error");
  }
};

const uploadService = {
  uploadFile,
  getList,
  getPublicFile,
  backupFiles,
};

export default uploadService;